import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, offerStatusLabel, type Offer, type OfferLogEntry, type PublicLinkResult, type SendOfferPreview } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { FollowUpPanel } from '../crm';
import { useSession } from '../../session';
import { offerTone } from './OffersPage';
import { LinesView } from './LinesEditor';
import { OfferOrderLink } from '../orders/RelatedOrders';
import './offers.css';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const day = (d: string) => dateFormat.format(new Date(`${d}T12:00:00`));

/**
 * Ansicht eines Angebots mit allen Schritten: Entwurf bearbeiten, abschließen, per E-Mail senden, PDF, Link zur Online-Annahme, Entscheidung eintragen, neue Fassung
 * (KON-098 bis KON-102). Was ein Schritt darf, entscheidet der Server, die Seite blendet nur aus, was nicht passt.
 */
export function OfferDetailPage() {
  const { id = '' } = useParams();
  const { can, hasModule } = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const offer = useQuery({ queryKey: ['offer', id], queryFn: async () => (await api<{ data: Offer }>('GET', `/offers/${id}`)).data });
  const set = (o: Offer) => {
    client.setQueryData(['offer', o.id], o);
    client.invalidateQueries({ queryKey: ['offers'] });
  };
  const run = useMutation({
    mutationFn: async (step: 'issue' | 'accepted' | 'rejected' | 'version' | 'link' | 'revoke' | 'delete') => {
      if (step === 'issue') return (await api<{ data: Offer }>('POST', `/offers/${id}/issue`)).data;
      if (step === 'accepted' || step === 'rejected') return (await api<{ data: Offer }>('POST', `/offers/${id}/decision`, { decision: step })).data;
      if (step === 'version') return (await api<{ data: Offer }>('POST', `/offers/${id}/versions`)).data;
      if (step === 'link') {
        setLink((await api<{ data: PublicLinkResult }>('POST', `/offers/${id}/link`)).data.url);
        return (await api<{ data: Offer }>('GET', `/offers/${id}`)).data;
      }
      if (step === 'revoke') return (await api<{ data: Offer }>('DELETE', `/offers/${id}/link`)).data;
      await api('DELETE', `/offers/${id}`);
      return null;
    },
    onSuccess: (o, step) => {
      setMessage(null);
      if (step === 'delete') {
        client.invalidateQueries({ queryKey: ['offers'] });
        navigate('/angebote', { replace: true });
        return;
      }
      if (step === 'version' && o) {
        client.invalidateQueries({ queryKey: ['offers'] });
        navigate(`/angebote/${o.id}/bearbeiten`);
        return;
      }
      if (o) set(o);
      if (step === 'revoke') setLink(null);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'draft_exists' && e.fields.draftId) navigate(`/angebote/${e.fields.draftId}`);
      else setMessage(e instanceof ApiError ? (e.status === 422 ? Object.values(e.fields)[0] ?? e.message : e.message) : 'Das hat nicht geklappt.');
    },
  });

  if (offer.isLoading) return <p className="muted">Wird geladen …</p>;
  if (offer.isError || !offer.data) {
    return (
      <div className="card narrow">
        <h1>Angebot nicht gefunden</h1>
        <Link to="/angebote">Zur Liste</Link>
      </div>
    );
  }
  const o = offer.data;
  const write = can('offers.write');
  const draft = o.status === 'draft';
  const open = o.status === 'sent' || o.status === 'expired';
  const confirm = (text: string, step: Parameters<typeof run.mutate>[0]) => window.confirm(text) && run.mutate(step);
  const r = o.recipient;

  return (
    <>
      <p><Link to="/angebote" className="back-link">← Alle Angebote</Link></p>
      <div className="page-head">
        <h1>{o.number ?? 'Entwurf'}{o.version > 1 && !o.number ? ` (Fassung ${o.version})` : ''}</h1>
        <span className={offerTone(o.status, o.sentVia)}>{offerStatusLabel(o.status, o.sentVia)}</span>
        <span className="muted">{o.subject}</span>
      </div>
      {o.supersededById && <div className="alert alert--warn">Dieses Angebot ist durch eine neuere Fassung ersetzt. <Link to={`/angebote/${o.supersededById}`}>Zur neuen Fassung</Link></div>}
      {o.decidedAt && (
        <div className={`alert ${o.status === 'accepted' ? 'alert--ok' : 'alert--error'}`}>
          {o.status === 'accepted' ? 'Angenommen' : 'Abgelehnt'} am {stamp.format(new Date(o.decidedAt))} Uhr
          {o.decidedVia === 'customer' ? ` vom Kunden online (${o.decidedByName})` : ` vom Büro (${o.decidedByName})`}
          {o.decisionNote ? `: „${o.decisionNote}“` : ''}
        </div>
      )}
      {o.status === 'accepted' && hasModule('orders') && can('orders.read') && <OfferOrderLink offerId={o.id} />}
      {message && <div className="alert alert--error" role="alert">{message}</div>}
      {link && (
        <div className="card invitation" role="status">
          <strong>Link zur Online-Annahme</strong>
          <p className="muted small">Der Link erscheint nur jetzt. Ein früherer Link ist damit ungültig.</p>
          <CopyField value={link} />
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => setLink(null)}>Schließen</button>
        </div>
      )}

      <div className="card offer-actions">
        {draft && write && <Link className="btn btn--secondary btn--sm" to={`/angebote/${o.id}/bearbeiten`}>Bearbeiten</Link>}
        {write && (draft || open) && <button type="button" className="btn btn--primary btn--sm" onClick={() => setSending(true)}>Per E-Mail senden</button>}
        {draft && write && o.inquirySource === 'app' && <button type="button" className="btn btn--primary btn--sm" disabled={run.isPending} onClick={() => confirm('An die Fahrgast-App senden?\n\nDas Angebot bekommt seine Nummer und lässt sich danach nicht mehr ändern, nur noch als neue Fassung. Der Fahrgast sieht es sofort in seiner App und kann es dort annehmen oder ablehnen. Eine E-Mail geht dabei nicht raus.', 'issue')}>An Fahrgast-App senden</button>}
        {draft && write && <button type="button" className="btn btn--secondary btn--sm" disabled={run.isPending} onClick={() => confirm('Als manuell versendet abschließen?\n\nSie schicken das PDF selbst, zum Beispiel per Post oder aus Ihrem E-Mail-Programm. Das Angebot bekommt seine Nummer und lässt sich danach nicht mehr ändern, nur noch als neue Fassung.', 'issue')}>Als manuell versendet abschließen</button>}
        <a className="btn btn--secondary btn--sm" href={`/api/v1/offers/${o.id}/pdf`} target="_blank" rel="noreferrer">{draft ? 'PDF-Vorschau' : 'PDF'}</a>
        {open && write && <button type="button" className="btn btn--secondary btn--sm" disabled={run.isPending} onClick={() => run.mutate('link')}>{o.hasPublicLink ? 'Neuen Link erzeugen' : 'Link zur Online-Annahme'}</button>}
        {o.hasPublicLink && write && !draft && <button type="button" className="btn btn--secondary btn--sm" disabled={run.isPending} onClick={() => confirm('Link sperren? Der Kunde kann das Angebot danach nicht mehr online öffnen.', 'revoke')}>Link sperren</button>}
        {open && write && <button type="button" className="btn btn--secondary btn--sm" disabled={run.isPending} onClick={() => confirm('Als angenommen eintragen?', 'accepted')}>Angenommen</button>}
        {open && write && <button type="button" className="btn btn--secondary btn--sm" disabled={run.isPending} onClick={() => confirm('Als abgelehnt eintragen?', 'rejected')}>Abgelehnt</button>}
        {!draft && write && (open || o.status === 'rejected') && <button type="button" className="btn btn--secondary btn--sm" disabled={run.isPending} onClick={() => run.mutate('version')}>Neue Fassung</button>}
        {draft && can('offers.delete') && <button type="button" className="btn btn--danger btn--sm" disabled={run.isPending} onClick={() => confirm('Entwurf löschen?', 'delete')}>Entwurf löschen</button>}
      </div>

      {sending && <SendPanel offer={o} onClose={() => setSending(false)} onSent={(sent) => { set(sent); setSending(false); }} />}

      <div className="card">
        <div className="offer-head">
          <div>
            <div className="muted small">Empfänger</div>
            <Link to={`/kontakte/${o.contactId}`} className="strong-link">{r.name}</Link>
            {r.company && r.company !== r.name && <div>{r.company}</div>}
            <div>{r.street}</div>
            <div>{[r.postalCode, r.city].filter(Boolean).join(' ')}</div>
            {r.email && <div className="muted small">{r.email}</div>}
          </div>
          <dl className="offer-meta">
            {o.issuedAt && (<><dt>Datum</dt><dd>{dateFormat.format(new Date(o.issuedAt))}</dd></>)}
            {o.validUntil && (<><dt>Gültig bis</dt><dd>{day(o.validUntil)}</dd></>)}
            {o.inquiryId && (<><dt>Anfrage</dt><dd><Link to={`/anfragen/${o.inquiryId}`}>{o.inquiryReference}</Link></dd></>)}
            {o.sentAt && (<><dt>Per E-Mail</dt><dd>{stamp.format(new Date(o.sentAt))} Uhr</dd></>)}
            {o.sentVia === 'manual' && (<><dt>Versand</dt><dd>manuell</dd></>)}
            {o.version > 1 && (<><dt>Fassung</dt><dd>{o.version}</dd></>)}
          </dl>
        </div>
        {o.introText && <p className="offer-text">{o.introText}</p>}
        <LinesView lines={o.lines} totals={o.totals} discountBp={o.discountBp} smallBusiness={o.smallBusiness} />
        {o.closingText && <p className="offer-text">{o.closingText}</p>}
      </div>

      <FollowUpPanel subjectKind="offer" subjectId={o.id} />
      <OfferLog offer={o} />
    </>
  );
}

const LOG_LABELS: Record<string, (d: Record<string, unknown>) => string> = {
  create: () => 'Angelegt',
  update: (d) => `Entwurf geändert${Object.keys(d).length ? `: ${Object.keys(d).map((k) => FIELD_LABELS[k] ?? k).join(', ')}` : ''}`,
  issue: (d) => `Abgeschlossen als ${d.number ?? 'Angebot'}${typeof d.grossCents === 'number' ? ` über ${formatCents(d.grossCents)}` : ''}`,
  sent_manually: () => 'Als manuell versendet markiert',
  send: (d) => `Per E-Mail versendet an ${d.to ?? 'Empfänger'}${d.withLink ? ', mit Link zur Online-Annahme' : ''}`,
  link_create: () => 'Link zur Online-Annahme erzeugt',
  link_revoke: () => 'Link zur Online-Annahme gesperrt',
  accept: (d) => `Angenommen${d.via === 'customer' ? ' vom Kunden online' : ' (im Büro eingetragen)'}${d.note ? `: „${d.note}“` : ''}`,
  reject: (d) => `Abgelehnt${d.via === 'customer' ? ' vom Kunden online' : ' (im Büro eingetragen)'}${d.note ? `: „${d.note}“` : ''}`,
  new_version: (d) => `Als neue Fassung ${d.version ?? ''} angelegt`.trim(),
};
const FIELD_LABELS: Record<string, string> = {
  contactId: 'Empfänger', inquiryId: 'Anfrage', subject: 'Betreff', introText: 'Einleitung', closingText: 'Schlusstext', validUntil: 'Gültig bis', discountBp: 'Rabatt', lines: 'Positionen',
};

/** Verlauf unten im Angebot (KON-114): wer wann was getan hat, aus dem Protokoll */
function OfferLog({ offer }: { offer: Offer }) {
  const log = useQuery({ queryKey: ['offer', offer.id, 'log', offer.updatedAt], queryFn: async () => (await api<{ data: OfferLogEntry[] }>('GET', `/offers/${offer.id}/log`)).data });
  return (
    <CollapsibleCard title="Verlauf" storageKey="offer-log">
      {log.isLoading && <p className="muted">Wird geladen …</p>}
      {log.isError && <p className="muted">Der Verlauf konnte nicht geladen werden.</p>}
      {log.data && log.data.length === 0 && <p className="muted">Noch keine Einträge.</p>}
      {log.data && log.data.length > 0 && (
        <ul className="history-list">
          {log.data.map((e) => (
            <li key={e.id}>
              <span className="history-list__when">{stamp.format(new Date(e.at))} Uhr</span>
              <span>
                {(LOG_LABELS[e.action] ?? (() => e.action))(e.data)}
                <span className="muted">{e.actorName ? ` von ${e.actorName}` : ' vom System'}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </CollapsibleCard>
  );
}

/** Versand per E-Mail (KON-101): Vorschlag aus den Firmendaten, vor dem Senden änderbar */
function SendPanel({ offer, onClose, onSent }: { offer: Offer; onClose: () => void; onSent: (o: Offer) => void }) {
  const preview = useQuery({ queryKey: ['offer-send', offer.id], queryFn: async () => (await api<{ data: SendOfferPreview }>('GET', `/offers/${offer.id}/send`)).data });
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [includeLink, setIncludeLink] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current || !preview.data) return;
    loaded.current = true;
    setTo(preview.data.to ?? '');
    setSubject(preview.data.subject);
    setBody(preview.data.body);
  }, [preview.data]);

  const send = useMutation({
    mutationFn: async () => (await api<{ data: Offer }>('POST', `/offers/${offer.id}/send`, { to: to.trim(), subject: subject.trim(), body, includeLink })).data,
    onSuccess: onSent,
    onError: (e) => setError(e instanceof ApiError ? (e.status === 422 ? Object.values(e.fields)[0] ?? e.message : e.message) : 'Die E-Mail konnte nicht gesendet werden.'),
  });

  if (preview.data && !preview.data.mailEnabled) {
    return (
      <div className="card">
        <h2>Per E-Mail senden</h2>
        <div className="alert alert--warn">Der Mailversand ist noch nicht eingerichtet. Laden Sie das PDF herunter und schicken Sie es selbst, oder schließen Sie das Angebot ohne Versand ab.</div>
        <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>Schließen</button>
      </div>
    );
  }
  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        send.mutate();
      }}
      noValidate
    >
      <h2>Per E-Mail senden</h2>
      {offer.status === 'draft' && <p className="muted small">Beim Senden wird der Entwurf abgeschlossen und bekommt seine Nummer. Danach lässt er sich nicht mehr ändern.</p>}
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <label className="field">
        <span>An</span>
        <input className="input" type="email" value={to} onChange={(e) => setTo(e.target.value)} autoComplete="off" />
      </label>
      <label className="field">
        <span>Betreff</span>
        <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} />
      </label>
      <label className="field">
        <span>Text</span>
        <textarea className="input textarea" rows={9} value={body} onChange={(e) => setBody(e.target.value)} maxLength={10000} />
        <small className="muted">Platzhalter: {'{name}'}, {'{nummer}'}, {'{firma}'}, {'{gueltig}'} und {'{link}'} für den Link zur Online-Annahme. Das PDF hängt Wirkia an.</small>
      </label>
      <label className="check">
        <input type="checkbox" checked={includeLink} onChange={(e) => setIncludeLink(e.target.checked)} />
        <span>Link zur Online-Annahme mitschicken (ersetzt einen früheren Link)</span>
      </label>
      <div className="form-actions">
        <button className="btn btn--primary" type="submit" disabled={send.isPending || !to.trim()}>{send.isPending ? 'Wird gesendet …' : 'Senden'}</button>
        <button className="btn btn--secondary" type="button" onClick={onClose}>Abbrechen</button>
      </div>
    </form>
  );
}

function CopyField({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="invitation__row">
      <input className="input" readOnly value={value} onFocus={(e) => e.currentTarget.select()} aria-label="Link" />
      <button
        type="button"
        className="btn btn--primary"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
          } catch {
            setCopied(false);
          }
        }}
      >
        {copied ? 'Kopiert' : 'Kopieren'}
      </button>
    </div>
  );
}
